import {
  AlertTriangle,
  Check,
  History,
  KeyRound,
  Lock,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  X,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import AdminPageLayout from "../../components/AdminPageLayout";
import ConfirmationModal from "../../components/ConfirmationModal";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import userConfigurationSchemaService, {
  ConfigurationSettingType,
  SchemaPropertyDescriptor,
  UserConfigurationSchema,
  UserConfigurationSchemaDescriptor,
  normalizeConfigurationSettingType,
} from "../../services/userConfigurationSchemaService";

const CONFIG_FIELD_MAX_LENGTH = 125;
const EDITOR_EMAIL = "shashank@runplutus.com";

type SettingScope = "root" | "userSettings";

interface BuiltInSetting {
  name: string;
  scope: SettingScope;
  type: ConfigurationSettingType;
  label: string;
  description: string;
}

interface DraftSetting {
  name: string;
  scope: SettingScope;
  type: ConfigurationSettingType;
  label: string;
  description?: string;
  builtIn: boolean;
}

interface RemoveTarget {
  name: string;
  scope: SettingScope;
}

interface AddSettingForm {
  scope: SettingScope;
  type: ConfigurationSettingType;
  label: string;
  key: string;
  advanced: boolean;
}

const SETTING_TYPES: Array<{
  type: ConfigurationSettingType;
  label: string;
  description: string;
}> = [
  { type: "boolean", label: "Boolean", description: "Yes or no value." },
  { type: "integer", label: "Integer", description: "Whole number value." },
  { type: "string", label: "String", description: "Short text value." },
];

const BUILT_IN_SETTINGS: BuiltInSetting[] = [
  {
    name: "isbeta",
    scope: "root",
    type: "boolean",
    label: "Beta Access",
    description: "Controls beta-only access checks.",
  },
  {
    name: "iscustomer",
    scope: "root",
    type: "boolean",
    label: "Customer Status",
    description: "Controls customer-only app surfaces.",
  },
  {
    name: "isretirement",
    scope: "root",
    type: "boolean",
    label: "Retirement Account",
    description: "Controls retirement agreement and short-position gates.",
  },
  {
    name: "tradingenabled",
    scope: "root",
    type: "boolean",
    label: "Admin Trading Enabled",
    description: "Admin kill switch for live trading.",
  },
  {
    name: "enableTradeNotification",
    scope: "root",
    type: "boolean",
    label: "Trade Notifications",
    description: "Allows portfolio update notifications for this user.",
  },
  {
    name: "user_tradingenabled",
    scope: "userSettings",
    type: "boolean",
    label: "User Trading Enabled",
    description: "User-controlled trading pause state.",
  },
  {
    name: "cash_based_deployment",
    scope: "userSettings",
    type: "boolean",
    label: "New Cash Deployment",
    description: "User election for auto-deploying new cash vs holding it in reserve.",
  },
];

const RESERVED_KEYS = new Set([
  "ibkr_account_id",
  "ibkraccountid",
  "portfolioweight",
  "user_settings",
  "usersettings",
]);

const getScopeLabel = (scope: SettingScope): string =>
  scope === "root" ? "Admin setting" : "User setting";

const scopeHelpText = (scope: SettingScope): string =>
  scope === "root"
    ? "Saved at data.<key> and editable by admins."
    : "Saved at data.userSettings.<key> and writable from user settings.";

function formatSettingLabel(name: string): string {
  return name
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function toSettingKey(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_")
    .slice(0, CONFIG_FIELD_MAX_LENGTH);
}

function uniqueByName(items: DraftSetting[]): DraftSetting[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = `${item.scope}:${item.name}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function descriptorToDraftSettings(
  descriptor: UserConfigurationSchemaDescriptor | null
): DraftSetting[] {
  const builtIns = BUILT_IN_SETTINGS.map((item): DraftSetting => ({
    ...item,
    builtIn: true,
  }));

  const fromProperties = (
    properties: SchemaPropertyDescriptor[] | undefined,
    scope: SettingScope
  ): DraftSetting[] =>
    (properties ?? []).map((property) => {
      const builtIn = BUILT_IN_SETTINGS.find(
        (item) => item.scope === scope && item.name === property.name
      );
      return {
        name: property.name,
        scope,
        type: builtIn?.type ?? normalizeConfigurationSettingType(property.type),
        label: builtIn?.label ?? formatSettingLabel(property.name),
        description: builtIn?.description,
        builtIn: Boolean(builtIn),
      };
    });

  return uniqueByName([
    ...builtIns,
    ...fromProperties(descriptor?.rootLevelProperties, "root"),
    ...fromProperties(descriptor?.userSettingsProperties, "userSettings"),
  ]).sort((a, b) => {
    if (a.scope !== b.scope) return a.scope === "root" ? -1 : 1;
    if (a.builtIn !== b.builtIn) return a.builtIn ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

function descriptorToComparableSettings(
  descriptor: UserConfigurationSchemaDescriptor | null
): DraftSetting[] {
  if (!descriptor) return [];

  const fromProperties = (
    properties: SchemaPropertyDescriptor[] | undefined,
    scope: SettingScope
  ): DraftSetting[] =>
    (properties ?? []).map((property) => {
      const builtIn = BUILT_IN_SETTINGS.find(
        (item) => item.scope === scope && item.name === property.name
      );
      return {
        name: property.name,
        scope,
        type: builtIn?.type ?? normalizeConfigurationSettingType(property.type),
        label: builtIn?.label ?? formatSettingLabel(property.name),
        description: builtIn?.description,
        builtIn: Boolean(builtIn),
      };
    });

  return uniqueByName([
    ...fromProperties(descriptor.rootLevelProperties, "root"),
    ...fromProperties(descriptor.userSettingsProperties, "userSettings"),
  ]).sort((a, b) => {
    if (a.scope !== b.scope) return a.scope === "root" ? -1 : 1;
    if (a.builtIn !== b.builtIn) return a.builtIn ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

function draftSettingsToDescriptor(settings: DraftSetting[]): UserConfigurationSchemaDescriptor {
  return {
    rootLevelProperties: settings
      .filter((setting) => setting.scope === "root")
      .map((setting) => ({ name: setting.name, type: setting.type })),
    userSettingsProperties: settings
      .filter((setting) => setting.scope === "userSettings")
      .map((setting) => ({ name: setting.name, type: setting.type })),
  };
}

function normalizeDescriptor(descriptor: UserConfigurationSchemaDescriptor | null): string {
  return JSON.stringify(draftSettingsToDescriptor(descriptorToComparableSettings(descriptor)));
}

function formatDateTime(value: string): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function extractErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message;
  if (
    err &&
    typeof err === "object" &&
    "response" in err &&
    typeof (err as { response?: { data?: { error?: unknown } } }).response?.data?.error === "string"
  ) {
    return String((err as { response: { data: { error: string } } }).response.data.error);
  }
  return fallback;
}

function validateSettingKey(
  key: string,
  existingSettings: DraftSetting[],
  _scope: SettingScope
): string | null {
  const trimmed = key.trim();
  if (!trimmed) return "Key is required.";
  if (trimmed.length > CONFIG_FIELD_MAX_LENGTH) {
    return `Key must be ${CONFIG_FIELD_MAX_LENGTH} characters or less.`;
  }
  if (!/^[a-z][a-z0-9_]*$/.test(trimmed)) {
    return "Use lowercase letters, numbers, and underscores. Start with a letter.";
  }
  if (RESERVED_KEYS.has(trimmed)) {
    return "This key is reserved.";
  }
  const isDuplicate = existingSettings.some((setting) => setting.name === trimmed);
  if (isDuplicate) return "A setting with this key already exists.";
  return null;
}

function countChanges(current: UserConfigurationSchema, previous?: UserConfigurationSchema): string {
  if (!previous) return "Initial version";
  const currentKeys = new Set([
    ...current.descriptor.rootLevelProperties.map((item) => `root:${item.name}:${normalizeConfigurationSettingType(item.type)}`),
    ...current.descriptor.userSettingsProperties.map((item) => `userSettings:${item.name}:${normalizeConfigurationSettingType(item.type)}`),
  ]);
  const previousKeys = new Set([
    ...previous.descriptor.rootLevelProperties.map((item) => `root:${item.name}:${normalizeConfigurationSettingType(item.type)}`),
    ...previous.descriptor.userSettingsProperties.map((item) => `userSettings:${item.name}:${normalizeConfigurationSettingType(item.type)}`),
  ]);
  let added = 0;
  let removed = 0;
  currentKeys.forEach((key) => {
    if (!previousKeys.has(key)) added += 1;
  });
  previousKeys.forEach((key) => {
    if (!currentKeys.has(key)) removed += 1;
  });
  if (added === 0 && removed === 0) return "No key changes";
  return `+${added} / -${removed}`;
}

const UserSettingsRegistryPage: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const canEdit = user?.email?.toLowerCase() === EDITOR_EMAIL;

  const [activeSchema, setActiveSchema] = useState<UserConfigurationSchema | null>(null);
  const [history, setHistory] = useState<UserConfigurationSchema[]>([]);
  const [settings, setSettings] = useState<DraftSetting[]>(() => descriptorToDraftSettings(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<RemoveTarget | null>(null);
  const [expandedHistoryId, setExpandedHistoryId] = useState<number | null>(null);
  const [form, setForm] = useState<AddSettingForm>({
    scope: "userSettings",
    type: "boolean",
    label: "",
    key: "",
    advanced: false,
  });

  const loadSchemas = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [activeResult, historyResult] = await Promise.allSettled([
        userConfigurationSchemaService.getActiveSchema(),
        userConfigurationSchemaService.getAllSchemas(),
      ]);

      if (historyResult.status === "rejected") {
        throw historyResult.reason;
      }

      const nextHistory = historyResult.value;
      setHistory(nextHistory);

      if (activeResult.status === "fulfilled") {
        setActiveSchema(activeResult.value);
        setSettings(descriptorToDraftSettings(activeResult.value.descriptor));
      } else {
        const status = (activeResult.reason as { response?: { status?: number } })?.response?.status;
        if (status !== 404) throw activeResult.reason;
        setActiveSchema(null);
        setSettings(descriptorToDraftSettings(null));
      }
    } catch (err) {
      setError(extractErrorMessage(err, "Failed to load user settings registry."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSchemas();
  }, [loadSchemas]);

  const baselineDescriptor = useMemo(
    () => normalizeDescriptor(activeSchema?.descriptor ?? null),
    [activeSchema]
  );

  const currentDescriptor = useMemo(
    () => JSON.stringify(draftSettingsToDescriptor(settings)),
    [settings]
  );

  const hasChanges = currentDescriptor !== baselineDescriptor;
  const rootSettings = settings.filter((setting) => setting.scope === "root");
  const userSettings = settings.filter((setting) => setting.scope === "userSettings");
  const keyError = validateSettingKey(form.key, settings, form.scope);
  const canAdd = canEdit && form.label.trim().length > 0 && keyError === null;

  const updateLabel = (label: string) => {
    setForm((prev) => ({
      ...prev,
      label,
      key: prev.advanced ? prev.key : toSettingKey(label),
    }));
  };

  const updateScope = (scope: SettingScope) => {
    setForm((prev) => ({ ...prev, scope }));
  };

  const updateSetting = (target: RemoveTarget, updates: Partial<Pick<DraftSetting, "scope" | "type">>) => {
    if (!canEdit) return;
    setSettings((prev) =>
      uniqueByName(
        prev.map((setting) => {
          if (setting.builtIn || setting.scope !== target.scope || setting.name !== target.name) {
            return setting;
          }
          return { ...setting, ...updates };
        })
      )
    );
  };

  const closeAddModal = () => {
    setAddOpen(false);
    setForm({ scope: "userSettings", type: "boolean", label: "", key: "", advanced: false });
  };

  const handleAddSetting = () => {
    if (!canAdd) return;
    setSettings((prev) =>
      uniqueByName([
        ...prev,
        {
          name: form.key.trim(),
          scope: form.scope,
          type: form.type,
          label: form.label.trim(),
          builtIn: false,
        },
      ])
    );
    closeAddModal();
  };

  const handleRemoveSetting = () => {
    if (!removeTarget) return;
    setSettings((prev) =>
      prev.filter(
        (setting) =>
          !(
            setting.scope === removeTarget.scope &&
            setting.name === removeTarget.name &&
            !setting.builtIn
          )
      )
    );
    setRemoveTarget(null);
  };

  const handleSave = async () => {
    if (!canEdit || saving || !hasChanges) return;
    try {
      setSaving(true);
      await userConfigurationSchemaService.createSchema(draftSettingsToDescriptor(settings));
      toast.success("User settings registry saved");
      await loadSchemas();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to save user settings registry."));
    } finally {
      setSaving(false);
    }
  };

  const actions = (
    <>
      <button
        type="button"
        onClick={loadSchemas}
        disabled={loading || saving}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator text-sm font-semibold text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 transition-colors disabled:opacity-50"
      >
        <RefreshCw className="w-4 h-4" />
        Refresh
      </button>
      {canEdit && (
        <button
          type="button"
          onClick={handleSave}
          disabled={!hasChanges || saving || loading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rp-amber hover:bg-yellow-600 text-white text-sm font-semibold transition-colors disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? "Saving..." : "Save Version"}
        </button>
      )}
    </>
  );

  return (
    <AdminPageLayout
      title="User Settings Registry"
      description={
        <span className="inline-flex items-center gap-2">
          <span>Active schema {activeSchema ? `#${activeSchema.id}` : "not created"}</span>
          <span className="px-2 py-0.5 bg-gray-100 dark:bg-rp-surface-raised text-gray-600 dark:text-gray-400 text-[10px] font-semibold uppercase tracking-wider rounded border border-gray-200 dark:border-gray-800">
            {canEdit ? "Editor" : "Read only"}
          </span>
        </span>
      }
      actions={actions}
    >
      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          <div className="h-10 bg-gray-200 dark:bg-gray-800 rounded animate-pulse" />
          <div className="h-40 bg-gray-200 dark:bg-gray-800 rounded animate-pulse" />
          <div className="h-40 bg-gray-200 dark:bg-gray-800 rounded animate-pulse" />
        </div>
      ) : (
        <div className="space-y-4">
          {!canEdit && (
            <div className="bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator rounded-lg px-4 py-3 flex items-center gap-3">
              <Lock className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Only {EDITOR_EMAIL} can edit this registry.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <SettingsTable
              title="Admin Settings"
              description="data.<key>"
              settings={rootSettings}
              canEdit={canEdit}
              onUpdate={updateSetting}
              onRemove={setRemoveTarget}
            />
            <SettingsTable
              title="User Settings"
              description="data.userSettings.<key>"
              settings={userSettings}
              canEdit={canEdit}
              onUpdate={updateSetting}
              onRemove={setRemoveTarget}
            />
          </div>

          {canEdit && (
            <div className="flex items-center justify-between gap-3 bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator rounded-lg px-4 py-3">
              <div className="flex items-center gap-2 min-w-0">
                <KeyRound className="w-4 h-4 text-rp-amber shrink-0" />
                <span className="text-sm text-gray-600 dark:text-gray-300 truncate">
                  {hasChanges ? "Unsaved schema version" : "No pending registry changes"}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setAddOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rp-amber hover:bg-yellow-600 text-white text-sm font-semibold transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Setting
              </button>
            </div>
          )}

          <HistoryTable
            history={history}
            expandedHistoryId={expandedHistoryId}
            onToggle={setExpandedHistoryId}
          />
        </div>
      )}

      <AddSettingModal
        isOpen={addOpen}
        form={form}
        keyError={keyError}
        canAdd={canAdd}
        onClose={closeAddModal}
        onLabelChange={updateLabel}
        onScopeChange={updateScope}
        onTypeChange={(type) => setForm((prev) => ({ ...prev, type }))}
        onKeyChange={(key) => setForm((prev) => ({ ...prev, key }))}
        onAdvancedChange={(advanced) =>
          setForm((prev) => ({
            ...prev,
            advanced,
            key: advanced ? prev.key : toSettingKey(prev.label),
          }))
        }
        onAdd={handleAddSetting}
      />

      <ConfirmationModal
        isOpen={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleRemoveSetting}
        title="Remove setting key"
        message={`Removing "${removeTarget?.name ?? ""}" from the registry can break future config writes for users who still have this key stored. Save a new version only after existing data is cleaned up.`}
        confirmText="Remove Key"
        isDestructive
      />
    </AdminPageLayout>
  );
};

interface SettingsTableProps {
  title: string;
  description: string;
  settings: DraftSetting[];
  canEdit: boolean;
  onUpdate: (target: RemoveTarget, updates: Partial<Pick<DraftSetting, "scope" | "type">>) => void;
  onRemove: (target: RemoveTarget) => void;
}

const SettingsTable: React.FC<SettingsTableProps> = ({
  title,
  description,
  settings,
  canEdit,
  onUpdate,
  onRemove,
}) => (
  <div className="bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator rounded-lg overflow-hidden">
    <div className="px-4 py-3 border-b border-gray-200 dark:border-rp-separator flex items-center justify-between gap-3">
      <div>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">{description}</p>
      </div>
      <span className="px-2 py-0.5 bg-gray-100 dark:bg-rp-surface-raised text-gray-600 dark:text-gray-400 text-[10px] font-semibold uppercase tracking-wider rounded border border-gray-200 dark:border-gray-800">
        {settings.length} keys
      </span>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[760px]">
        <thead>
          <tr className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-rp-separator">
            <th className="text-left px-4 py-2.5">Setting</th>
            <th className="text-left px-4 py-2.5">Key</th>
            <th className="text-left px-4 py-2.5">Location</th>
            <th className="text-left px-4 py-2.5">Type</th>
            <th className="text-left px-4 py-2.5">Status</th>
            <th className="text-right px-4 py-2.5">Action</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-rp-separator">
          {settings.map((setting) => (
            <tr
              key={`${setting.scope}-${setting.name}`}
              className="hover:bg-gray-50 dark:hover:bg-rp-surface-raised transition-colors"
            >
              <td className="px-4 py-3">
                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                  {setting.label}
                </div>
                {setting.description && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {setting.description}
                  </div>
                )}
              </td>
              <td className="px-4 py-3 font-mono text-gray-900 dark:text-white">
                {setting.name}
              </td>
              <td className="px-4 py-3">
                {canEdit && !setting.builtIn ? (
                  <select
                    value={setting.scope}
                    onChange={(event) =>
                      onUpdate(
                        { name: setting.name, scope: setting.scope },
                        { scope: event.target.value as SettingScope }
                      )
                    }
                    className="bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator rounded-lg px-2 py-1 text-xs font-semibold text-gray-700 dark:text-gray-300 focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber transition-all"
                    aria-label={`Location for ${setting.name}`}
                  >
                    <option value="root">Admin</option>
                    <option value="userSettings">User</option>
                  </select>
                ) : (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {setting.scope === "root" ? "Admin" : "User"}
                  </span>
                )}
              </td>
              <td className="px-4 py-3">
                {canEdit && !setting.builtIn ? (
                  <select
                    value={setting.type}
                    onChange={(event) =>
                      onUpdate(
                        { name: setting.name, scope: setting.scope },
                        { type: event.target.value as ConfigurationSettingType }
                      )
                    }
                    className="bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator rounded-lg px-2 py-1 text-xs font-semibold text-gray-700 dark:text-gray-300 focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber transition-all"
                    aria-label={`Type for ${setting.name}`}
                  >
                    {SETTING_TYPES.map((item) => (
                      <option key={item.type} value={item.type}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {SETTING_TYPES.find((item) => item.type === setting.type)?.label ?? "Boolean"}
                  </span>
                )}
              </td>
              <td className="px-4 py-3">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 dark:bg-rp-surface-raised text-gray-600 dark:text-gray-400 text-[10px] font-semibold uppercase tracking-wider rounded border border-gray-200 dark:border-gray-800">
                  {setting.builtIn && <Lock className="w-3 h-3" />}
                  {setting.builtIn ? "Built in" : "Custom"}
                </span>
              </td>
              <td className="px-4 py-3 text-right">
                {canEdit && !setting.builtIn ? (
                  <button
                    type="button"
                    onClick={() => onRemove({ name: setting.name, scope: setting.scope })}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-red-600 dark:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    title="Remove setting"
                    aria-label={`Remove ${setting.name}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                ) : (
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    Locked
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

interface AddSettingModalProps {
  isOpen: boolean;
  form: AddSettingForm;
  keyError: string | null;
  canAdd: boolean;
  onClose: () => void;
  onLabelChange: (value: string) => void;
  onScopeChange: (scope: SettingScope) => void;
  onTypeChange: (type: ConfigurationSettingType) => void;
  onKeyChange: (value: string) => void;
  onAdvancedChange: (value: boolean) => void;
  onAdd: () => void;
}

const AddSettingModal: React.FC<AddSettingModalProps> = ({
  isOpen,
  form,
  keyError,
  canAdd,
  onClose,
  onLabelChange,
  onScopeChange,
  onTypeChange,
  onKeyChange,
  onAdvancedChange,
  onAdd,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-rp-surface-raised rounded-lg shadow-2xl max-w-lg w-full overflow-hidden border border-gray-200 dark:border-rp-separator">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-rp-separator">
          <h2 className="text-base font-bold text-gray-900 dark:text-white">
            Add Setting
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-rp-surface transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
              Location
            </label>
            <div className="rounded-lg border border-gray-200 dark:border-rp-separator bg-white dark:bg-rp-surface p-0.5 grid grid-cols-2 gap-1">
              {(["userSettings", "root"] as SettingScope[]).map((scope) => {
                const isActive = form.scope === scope;
                return (
                  <button
                    key={scope}
                    type="button"
                    onClick={() => onScopeChange(scope)}
                    className={`px-3 py-2 text-sm font-semibold rounded-md transition-colors ${
                      isActive
                        ? "bg-rp-amber text-white"
                        : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                    }`}
                  >
                    {getScopeLabel(scope)}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {scopeHelpText(form.scope)}
            </p>
          </div>

          <div>
            <label htmlFor="setting-label" className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
              Display Name
            </label>
            <input
              id="setting-label"
              type="text"
              value={form.label}
              onChange={(event) => onLabelChange(event.target.value)}
              placeholder="Tax Loss Harvesting Enabled"
              className="w-full bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber transition-all"
            />
          </div>

          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
              Type
            </label>
            <div className="rounded-lg border border-gray-200 dark:border-rp-separator bg-white dark:bg-rp-surface p-0.5 grid grid-cols-3 gap-1">
              {SETTING_TYPES.map((item) => {
                const isActive = form.type === item.type;
                return (
                  <button
                    key={item.type}
                    type="button"
                    onClick={() => onTypeChange(item.type)}
                    className={`px-3 py-2 text-sm font-semibold rounded-md transition-colors ${
                      isActive
                        ? "bg-rp-amber text-white"
                        : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                    }`}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {SETTING_TYPES.find((item) => item.type === form.type)?.description}
            </p>
          </div>

          <label className="flex items-center gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={form.advanced}
              onChange={(event) => onAdvancedChange(event.target.checked)}
              className="hidden"
            />
            <div
              className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                form.advanced
                  ? "bg-rp-amber border-rp-amber"
                  : "border-gray-300 dark:border-rp-separator group-hover:border-rp-amber"
              }`}
            >
              {form.advanced && <Check className="w-3 h-3 text-white" />}
            </div>
            <span className="text-sm text-gray-600 dark:text-gray-300">
              Edit generated key
            </span>
          </label>

          <div>
            <label htmlFor="setting-key" className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
              Key
            </label>
            <input
              id="setting-key"
              type="text"
              value={form.key}
              onChange={(event) => onKeyChange(event.target.value)}
              disabled={!form.advanced}
              className="w-full bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator rounded-lg px-3 py-2 text-sm font-mono text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber transition-all disabled:opacity-70"
            />
            {keyError ? (
              <p className="mt-2 text-xs text-red-600 dark:text-red-500">{keyError}</p>
            ) : (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Preview: {form.scope === "root" ? "data" : "data.userSettings"}.{form.key || "key"}
              </p>
            )}
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg px-3 py-2 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-700 dark:text-yellow-400 mt-0.5 shrink-0" />
            <p className="text-xs text-yellow-800 dark:text-yellow-300">
              Registry changes allow storage only. App behavior still needs implementation.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-rp-separator">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onAdd}
            disabled={!canAdd}
            className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-rp-amber hover:bg-yellow-600 transition-colors disabled:opacity-50"
          >
            Add Setting
          </button>
        </div>
      </div>
    </div>
  );
};

interface HistoryTableProps {
  history: UserConfigurationSchema[];
  expandedHistoryId: number | null;
  onToggle: (id: number | null) => void;
}

const HistoryTable: React.FC<HistoryTableProps> = ({
  history,
  expandedHistoryId,
  onToggle,
}) => (
  <div className="bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator rounded-lg overflow-hidden">
    <div className="px-4 py-3 border-b border-gray-200 dark:border-rp-separator flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <History className="w-4 h-4 text-rp-amber" />
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
          Version History
        </h2>
      </div>
      <span className="px-2 py-0.5 bg-gray-100 dark:bg-rp-surface-raised text-gray-600 dark:text-gray-400 text-[10px] font-semibold uppercase tracking-wider rounded border border-gray-200 dark:border-gray-800">
        {history.length} versions
      </span>
    </div>

    {history.length === 0 ? (
      <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
        No schema versions.
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-rp-separator">
              <th className="text-left px-4 py-2.5">Version</th>
              <th className="text-left px-4 py-2.5">Updated</th>
              <th className="text-right px-4 py-2.5">Admin Settings</th>
              <th className="text-right px-4 py-2.5">User Settings</th>
              <th className="text-left px-4 py-2.5">Changes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-rp-separator">
            {history.map((schema, index) => {
              const isExpanded = expandedHistoryId === schema.id;
              return (
                <React.Fragment key={schema.id}>
                  <tr
                    onClick={() => onToggle(isExpanded ? null : schema.id)}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-rp-surface-raised transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-gray-900 dark:text-white">#{schema.id}</span>
                        {index === 0 && (
                          <span className="px-2 py-0.5 bg-gray-100 dark:bg-rp-surface-raised text-gray-600 dark:text-gray-400 text-[10px] font-semibold uppercase tracking-wider rounded border border-gray-200 dark:border-gray-800">
                            Active
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {formatDateTime(schema.updatedAtUtc)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-gray-900 dark:text-white">
                      {schema.descriptor.rootLevelProperties.length}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-gray-900 dark:text-white">
                      {schema.descriptor.userSettingsProperties.length}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {countChanges(schema, history[index + 1])}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={5} className="px-4 py-3 bg-gray-50 dark:bg-rp-surface-sunken">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <HistoryKeyList
                            title="Admin Settings"
                            keys={schema.descriptor.rootLevelProperties}
                          />
                          <HistoryKeyList
                            title="User Settings"
                            keys={schema.descriptor.userSettingsProperties}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const HistoryKeyList: React.FC<{ title: string; keys: SchemaPropertyDescriptor[] }> = ({
  title,
  keys,
}) => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
      {title}
    </div>
    <div className="flex flex-wrap gap-2">
      {keys.length === 0 ? (
        <span className="text-xs text-gray-500 dark:text-gray-400">No keys</span>
      ) : (
        keys.map((key) => (
          <span
            key={key.name}
            className="px-2 py-0.5 bg-gray-100 dark:bg-rp-surface-raised text-gray-600 dark:text-gray-400 text-[10px] font-semibold rounded border border-gray-200 dark:border-gray-800 font-mono"
          >
            {key.name}
            <span className="ml-1 uppercase tracking-wider text-gray-400 dark:text-gray-500">
              {normalizeConfigurationSettingType(key.type)}
            </span>
          </span>
        ))
      )}
    </div>
  </div>
);

export {
  BUILT_IN_SETTINGS,
  EDITOR_EMAIL,
  descriptorToDraftSettings,
  draftSettingsToDescriptor,
  toSettingKey,
  validateSettingKey,
};

export default UserSettingsRegistryPage;
